/**
 * The Discover layout document (#2291), as the page reads and changes it.
 *
 * Every change is a function from one document to the next
 * (`LayoutChange`), never a patch: the store applies it to the document it
 * last read, sends the whole result, and on a 409 applies the same change to
 * the server's newer copy. So a change must make sense on a document it has
 * not seen; each one here looks its subject up by id and does nothing (or
 * refuses, in plain words) when it is gone.
 *
 * Sections of a type this build does not know are carried through every
 * change untouched (the server keeps them only if they come back exactly as
 * stored), and are never drawn.
 *
 * What a document may hold (types, variants, presets, limits) is read from
 * the registry the server answered it with (`registryOf`, #2298).
 */
import {
  DISCOVER_LIMITS,
  DISCOVER_SCHEMA,
  type DiscoverLayout,
  type DiscoverPreset,
  type DiscoverSection,
  type DiscoverSectionType,
  type DiscoverVariant,
  type FilterBoxMode,
  type ModelQueryJson,
  type ModelQueryRange,
  type SavedSearch,
} from '@/api/discoverPreferences';
import { MAX_QUERY_TEXT } from '@/types/modelQuery';
import type { FavoriteKind, FeaturedSource } from '../favorites';
import {
  FILTER_BOX_LABELS,
  SOURCES_FOR,
  allowedOf,
  catalogEntry,
  choicesOf,
  catalogOf,
  drawsType,
  presetOf,
  registryOf,
  variantsOf,
  type BuiltInType,
  type NamedPreset,
} from './discoverRegistry';
import {
  EMPTY_QUERY,
  andGroups,
  dedupeGroups,
  describeDate,
  describeGroups,
  toModelQuery,
  type DateFilter,
  type EntityLookup,
  type Groups,
  type SearchQuery,
} from '../search/searchSyntax';
import { explainProblem } from '../search/queryProblems';

/**
 * The built-in default: today's fixed page, the default preset of this
 * build's copy of the registry, so the page can draw it before (or without)
 * an answer. The server answers its own default with its registry.
 */
export const DEFAULT_LAYOUT: DiscoverLayout = {
  schema: DISCOVER_SCHEMA,
  rev: 0,
  preset: 'default',
  searches: [],
  sections: presetOf('default')!.sections.map((s) => structuredClone(s)),
  source: 'default',
};

/** Where "Place it" puts a new section. */
export type Placement = 'top' | 'afterFavorites' | 'bottom';

export const PLACEMENTS: { value: Placement; label: string }[] = [
  { value: 'top', label: 'Top' },
  { value: 'afterFavorites', label: 'After favorites' },
  { value: 'bottom', label: 'Bottom' },
];

/**
 * "Show as": the variants a saved search may be drawn as, in words (this
 * build's copy of the registry: the dialog asks before any answer; a variant
 * the document's registry refuses falls back to its first, `savedSection`).
 */
export const SHOW_AS: { value: DiscoverVariant; label: string }[] = (
  [
    { value: 'carousel', label: 'Shelf' },
    { value: 'list', label: 'List' },
    { value: 'grid', label: 'Grid' },
  ] as { value: DiscoverVariant; label: string }[]
).filter((o) => variantsOf('savedSearch').includes(o.value));

/** How many a new saved-search section shows: the built-in sections' counts for each look. */
export const SAVED_COUNT: Record<'carousel' | 'grid' | 'list', number> = { carousel: 14, grid: 9, list: 8 };

/**
 * A change to the document. Any change by hand freezes a preset into
 * `custom`; a change that chooses a layout names the preset it leaves the
 * document on (`setsPreset`), and the store stores that instead.
 */
export interface LayoutChange {
  (layout: DiscoverLayout): DiscoverLayout;
  setsPreset?: DiscoverPreset;
}

/** A change that cannot apply to the document as it is: why, in plain words. */
export class LayoutChangeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LayoutChangeError';
  }
}

// ── Reading ─────────────────────────────────────────────────────────────

/** A type this build draws and the document's registry accepts. */
export function isKnownType(type: string, layout?: DiscoverLayout): type is DiscoverSectionType {
  return drawsType(type, registryOf(layout));
}

/** The variant to draw a section as: its own when its type allows it, else the type's first. */
export function variantOf(section: DiscoverSection, layout?: DiscoverLayout): DiscoverVariant {
  const allowed = variantsOf(section.type, registryOf(layout));
  return (allowed as readonly string[]).includes(section.variant)
    ? (section.variant as DiscoverVariant)
    : allowed[0] ?? 'carousel';
}

/** The most items a section may show: the document's registry's. */
export function maxCountOf(layout?: DiscoverLayout): number {
  return registryOf(layout).limits.count;
}

/** The section's count, kept to the server's range. */
export function countOf(section: DiscoverSection, layout?: DiscoverLayout): number {
  const min = DISCOVER_LIMITS.count.min;
  const n = Math.round(Number(section.count));
  if (!Number.isFinite(n)) return min;
  return Math.min(maxCountOf(layout), Math.max(min, n));
}

/** Whether the page draws a section: shown, of a known type, and (for a saved search) naming one. */
export function isDrawn(section: DiscoverSection, layout: DiscoverLayout): boolean {
  if (section.hidden || !isKnownType(section.type, layout)) return false;
  if (section.type === 'savedSearch') return !!searchOf(layout, section.settings?.searchId);
  return true;
}

/** The sections the page draws, in order. */
export function drawnSections(layout: DiscoverLayout): DiscoverSection[] {
  return layout.sections.filter((s) => isDrawn(s, layout));
}

export function searchOf(layout: DiscoverLayout, id: string | undefined): SavedSearch | undefined {
  return id ? layout.searches.find((s) => s.id === id) : undefined;
}

/** The sections that show a saved search. */
export function sectionsOfSearch(layout: DiscoverLayout, searchId: string): DiscoverSection[] {
  return layout.sections.filter((s) => s.type === 'savedSearch' && s.settings?.searchId === searchId);
}

/** Whether a saved search is on the page. */
export function isOnDiscover(layout: DiscoverLayout, searchId: string): boolean {
  return sectionsOfSearch(layout, searchId).some((s) => !s.hidden);
}

/** What the featured carousel draws from, from its `sources` setting. */
export function featuredSourceOf(section: DiscoverSection): FeaturedSource {
  const sources = section.settings?.sources ?? ['editorsPicks'];
  const picks = sources.includes('editorsPicks');
  const favorites = sources.includes('favorites');
  if (picks && favorites) return 'mixed';
  return favorites ? 'favorites' : 'picks';
}

/** The filter box settings the document's registry allows, in its order: none from a server without them (#2302). */
export function filterBoxModesOf(layout?: DiscoverLayout): FilterBoxMode[] {
  return choicesOf('savedSearch', 'filterBox', registryOf(layout))
    .filter((m): m is FilterBoxMode => m in FILTER_BOX_LABELS);
}

/** A saved-search section's filter box: as stored, when the registry allows it; else (and when absent) auto. */
export function filterBoxOf(section: DiscoverSection, layout?: DiscoverLayout): FilterBoxMode {
  const mode = section.settings?.filterBox;
  return mode && filterBoxModesOf(layout).includes(mode) ? mode : 'auto';
}

/** Two documents that would store the same thing. */
export function sameLayout(a: DiscoverLayout, b: DiscoverLayout): boolean {
  return a.preset === b.preset
    && JSON.stringify(a.searches) === JSON.stringify(b.searches)
    && JSON.stringify(a.sections) === JSON.stringify(b.sections);
}

// ── Queries ─────────────────────────────────────────────────────────────

function fromRange(r: ModelQueryRange | null | undefined): DateFilter | null {
  if (!r) return null;
  const within = (r as { within?: string | null }).within;
  if (within) return { within: within as Extract<DateFilter, { within: unknown }>['within'] };
  const { from, to } = r as { from?: string | null; to?: string | null };
  if (!from && !to) return null;
  return { ...(from ? { from } : {}), ...(to ? { to } : {}) };
}

/**
 * A saved query (the server's canonical form, absent parts as null) as the
 * search box holds it.
 */
export function fromModelQuery(m: ModelQueryJson): SearchQuery {
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
  return {
    ...EMPTY_QUERY,
    text: typeof m.text === 'string' ? m.text : '',
    tags: { all: list(m.tags?.all), any: list(m.tags?.any), none: list(m.tags?.none) },
    authors: list(m.authors),
    updated: fromRange(m.updated),
    created: fromRange(m.created),
    editorsPick: m.editorsPick === true,
    shared: m.shared ?? null,
    // a search stored before #2299 names one functional as usesFunctional
    functionals: groupsOf(m.functionals, m.usesFunctional),
    submodels: groupsOf(m.submodels),
    sort: m.sort ?? null,
  };
}

/** Stored groups as the box holds them; `first`, the legacy single functional, ANDed into each. */
function groupsOf(stored: unknown, first?: string | null): Groups {
  const groups: Groups = Array.isArray(stored)
    ? stored.filter(Array.isArray).map((g: unknown[]) => g.map((id) => String(id).toLowerCase()))
    : [];
  return first ? andGroups([[first.toLowerCase()]], groups) : dedupeGroups(groups);
}

/**
 * A saved query narrowed by a section's filter box (#2302): the typed text
 * joined to the saved text with a space, every other filter as saved. The
 * server's text search quotes each word of three letters or more, and Mongo
 * ANDs quoted words, so a model must match every such word of both; its other
 * match, a name that starts with the whole text, narrows the same way. A
 * shorter typed word narrows only that name match.
 */
export function withFilterText(q: SearchQuery, typed: string): SearchQuery {
  const text = typed.trim();
  if (!text) return q;
  return { ...q, text: q.text.trim() ? `${q.text.trim()} ${text}` : text };
}

/** How long the filter box's text may be: what the query's text limit leaves after the saved text and a space. */
export function filterTextRoom(q: SearchQuery): number {
  const saved = q.text.trim();
  return Math.max(0, MAX_QUERY_TEXT - (saved ? saved.length + 1 : 0));
}

/** The search box's query in the stored format. */
export function toSavedQuery(q: SearchQuery): ModelQueryJson {
  return toModelQuery(q) as ModelQueryJson;
}

/** Whether two saved queries are the same search. */
export function sameQuery(a: ModelQueryJson, b: ModelQueryJson): boolean {
  return JSON.stringify(toSavedQuery(fromModelQuery(a))) === JSON.stringify(toSavedQuery(fromModelQuery(b)));
}

const WITHIN_WORDS: Record<string, string> = { '7d': '7 days', '30d': '30 days', '90d': '90 days', '1y': '1 year' };

function dateWords(f: DateFilter): string {
  return 'within' in f ? WITHIN_WORDS[f.within] ?? f.within : describeDate(f);
}

/**
 * A search in a few words, for a new saved search's title:
 * "berth · port-ops · 30 days". At most the title's length.
 */
export function summarizeQuery(q: SearchQuery, lookup?: Pick<EntityLookup, 'labelOf'>): string {
  const name = (kind: 'author' | 'functional' | 'model' | 'group', id: string) => lookup?.labelOf(kind, id) ?? null;
  const parts: string[] = [];
  if (q.text.trim()) parts.push(q.text.trim());
  parts.push(...q.tags.all);
  if (q.tags.any.length) parts.push(q.tags.any.join(' or '));
  parts.push(...q.tags.none.map((t) => `not ${t}`));
  for (const a of q.authors) parts.push(a === 'me' ? 'mine' : `by ${name('author', a) ?? 'someone'}`);
  if (q.updated) parts.push(dateWords(q.updated));
  if (q.created) parts.push(`created ${dateWords(q.created)}`);
  if (q.editorsPick) parts.push("editor's picks");
  if (q.shared) parts.push(q.shared === 'me' ? 'shared with me' : `shared with ${name('group', q.shared) ?? 'a group'}`);
  if (q.functionals.length) parts.push(`uses ${describeGroups(q.functionals, (id) => name('functional', id) ?? 'a functional')}`);
  if (q.submodels.length) parts.push(`uses ${describeGroups(q.submodels, (id) => name('model', id) ?? 'a model')}`);

  const max = DISCOVER_LIMITS.title;
  let out = '';
  for (const p of parts) {
    const next = out ? `${out} · ${p}` : p;
    if (next.length > max) {
      if (!out) out = `${p.slice(0, max - 1).trimEnd()}…`;
      break;
    }
    out = next;
  }
  return out || 'Saved search';
}

// ── Ids ─────────────────────────────────────────────────────────────────

const rand = () => Math.random().toString(36).slice(2, 8).padEnd(6, '0'); // NOSONAR typescript:S2245 non-crypto-random: saved-search UI id suffix, not a secret

/** A new search id from its title: `berth-port-ops-4k2j9x`, `[a-z0-9-]{1,32}`. */
export function newSearchId(title: string, taken: Iterable<string> = []): string {
  const used = new Set(taken);
  const slug = title.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24).replace(/-+$/, ''); // NOSONAR typescript:S8786 reliability-by-design: slug normalisation; the ^-+/-+$ strips are single anchored quantifiers (linear, no overlap) and the last runs on a <=24-char slice
  for (;;) {
    const id = slug ? `${slug}-${rand()}` : `search-${rand()}`;
    if (!used.has(id)) return id;
  }
}

function newSectionId(taken: Iterable<string>): string {
  const used = new Set(taken);
  for (;;) {
    const id = `saved-${rand()}`;
    if (!used.has(id)) return id;
  }
}

// ── Changes ─────────────────────────────────────────────────────────────

/** Where a new section goes. */
function insertAt(sections: DiscoverSection[], place: Placement): number {
  if (place === 'top') return 0;
  if (place === 'afterFavorites') {
    const at = sections.findIndex((s) => s.type === 'favorites');
    if (at >= 0) return at + 1;
  }
  return sections.length;
}

function savedSection(layout: DiscoverLayout, searchId: string, variant: DiscoverVariant): DiscoverSection {
  // a variant the registry allows for a saved search, else its first
  const allowed = variantsOf('savedSearch', registryOf(layout));
  const look = allowed.includes(variant) ? variant : allowed[0] ?? 'carousel';
  const shelf = (['carousel', 'grid', 'list'] as const).find((v) => v === look) ?? 'carousel';
  return {
    id: newSectionId(layout.sections.map((s) => s.id)),
    type: 'savedSearch',
    variant: look,
    count: Math.min(SAVED_COUNT[shelf], maxCountOf(layout)),
    hidden: false,
    settings: { searchId },
  };
}

function withSection(layout: DiscoverLayout, section: DiscoverSection, place: Placement): DiscoverLayout {
  const max = registryOf(layout).limits.sections;
  if (layout.sections.length >= max) {
    throw new LayoutChangeError(
      `Discover shows at most ${max} sections. Remove one from Discover first, or save without showing it.`,
    );
  }
  const sections = [...layout.sections];
  sections.splice(insertAt(sections, place), 0, section);
  return { ...layout, sections };
}

export interface NewSearch {
  /** Minted once, so a retry after a conflict adds the same search. */
  id: string;
  title: string;
  description?: string;
  query: ModelQueryJson;
  show: boolean;
  place: Placement;
  variant: DiscoverVariant;
}

const clean = (s: string | undefined) => (s ?? '').trim();

/** Save a new search, and (when `show`) its section. */
export function addSearch(n: NewSearch): LayoutChange {
  return (layout) => {
    const max = registryOf(layout).limits.searches;
    if (layout.searches.length >= max) {
      throw new LayoutChangeError(
        `You can keep up to ${max} saved searches. Delete one to save another.`,
      );
    }
    const id = layout.searches.some((s) => s.id === n.id) ? newSearchId(n.title, layout.searches.map((s) => s.id)) : n.id;
    const description = clean(n.description);
    const search: SavedSearch = { id, title: clean(n.title), ...(description ? { description } : {}), query: n.query };
    const next = { ...layout, searches: [...layout.searches, search] };
    return n.show ? withSection(next, savedSection(next, id, n.variant), n.place) : next;
  };
}

function editSearch(id: string, edit: (s: SavedSearch) => SavedSearch): LayoutChange {
  return (layout) => {
    if (!searchOf(layout, id)) {
      throw new LayoutChangeError('That saved search was deleted, perhaps on another device.');
    }
    return { ...layout, searches: layout.searches.map((s) => (s.id === id ? edit(s) : s)) };
  };
}

/** "Update saved search": a new query, the same title and description. */
export function updateSearchQuery(id: string, query: ModelQueryJson): LayoutChange {
  return editSearch(id, (s) => ({ ...s, query }));
}

/** Rename: a new title and description, the same query. */
export function renameSearch(id: string, title: string, description: string): LayoutChange {
  return editSearch(id, (s) => {
    const d = clean(description);
    const { description: _old, ...rest } = s;
    return { ...rest, title: clean(title), ...(d ? { description: d } : {}) };
  });
}

/** Delete a search and every section that shows it, in one write. */
export function deleteSearch(id: string): LayoutChange {
  return (layout) => ({
    ...layout,
    searches: layout.searches.filter((s) => s.id !== id),
    sections: layout.sections.filter((s) => !(s.type === 'savedSearch' && s.settings?.searchId === id)),
  });
}

/** "Show on Discover": its hidden section shown again, else a new one. */
export function showSearch(id: string, place: Placement = 'bottom', variant: DiscoverVariant = 'carousel'): LayoutChange {
  return (layout) => {
    if (!searchOf(layout, id)) {
      throw new LayoutChangeError('That saved search was deleted, perhaps on another device.');
    }
    if (isOnDiscover(layout, id)) return layout;
    const hidden = sectionsOfSearch(layout, id)[0];
    if (hidden) {
      return { ...layout, sections: layout.sections.map((s) => (s.id === hidden.id ? { ...s, hidden: false } : s)) };
    }
    return withSection(layout, savedSection(layout, id, variant), place);
  };
}

/** "Remove from Discover" in the Saved list: every section showing it goes; the search stays. */
export function removeSearchFromDiscover(id: string): LayoutChange {
  return (layout) => ({
    ...layout,
    sections: layout.sections.filter((s) => !(s.type === 'savedSearch' && s.settings?.searchId === id)),
  });
}

/** "Remove from Discover" on a section: that section goes; its search stays. */
export function removeSection(sectionId: string): LayoutChange {
  return (layout) => ({ ...layout, sections: layout.sections.filter((s) => s.id !== sectionId) });
}

/**
 * Move a section one place up or down the page: past the next section that
 * is drawn, so hidden and unknown sections never swallow a click.
 */
export function moveSection(sectionId: string, dir: -1 | 1): LayoutChange {
  return (layout) => {
    const from = layout.sections.findIndex((s) => s.id === sectionId);
    if (from < 0) return layout;
    let to = from + dir;
    while (to >= 0 && to < layout.sections.length && !isDrawn(layout.sections[to], layout)) to += dir;
    if (to < 0 || to >= layout.sections.length) return layout;
    const sections = [...layout.sections];
    const [moved] = sections.splice(from, 1);
    sections.splice(to, 0, moved);
    return { ...layout, sections };
  };
}

// ── Presets and the catalog (#2292) ─────────────────────────────────────

const copy = (s: DiscoverSection): DiscoverSection => structuredClone(s);

/**
 * The document as a profile on a preset sees it: a preset is a living
 * default, so each section type the preset (as the server defines it, in the
 * registry the document came with) has and the document lacks is added
 * where the preset puts it. A `custom` document is drawn exactly as stored;
 * it learns of new types from the "New section available" chip instead
 * (`missingTypes`).
 */
export function resolveLayout(layout: DiscoverLayout): DiscoverLayout {
  const preset = layout.preset === 'custom' ? undefined : presetOf(layout.preset, registryOf(layout));
  if (!preset) return layout;
  const have = new Set(layout.sections.map((s) => s.type));
  const missing = preset.sections.filter((s) => !have.has(s.type));
  if (!missing.length) return layout;
  const sections = [...layout.sections];
  preset.sections.forEach((s, i) => {
    if (!have.has(s.type)) sections.splice(Math.min(i, sections.length), 0, copy(s));
  });
  return { ...layout, sections };
}

/** The built-in types the document has no section of, in catalog order. */
export function missingTypes(layout: DiscoverLayout): BuiltInType[] {
  const have = new Set(layout.sections.map((s) => s.type));
  return catalogOf(registryOf(layout)).filter((e) => !have.has(e.type)).map((e) => e.type);
}

/** Which of Discover's four lists the drawn sections need: a hidden section asks for nothing. */
export interface ListsNeeded {
  trending: boolean;
  picks: boolean;
  recent: boolean;
  favorites: boolean;
}

export const NO_LISTS: ListsNeeded = { trending: false, picks: false, recent: false, favorites: false };

export function listsFor(layout: DiscoverLayout): ListsNeeded {
  const need = { ...NO_LISTS };
  for (const s of drawnSections(layout)) {
    switch (s.type as DiscoverSectionType) {
      case 'featured': {
        const source = featuredSourceOf(s);
        if (source !== 'favorites') need.picks = true;
        if (source !== 'picks') need.favorites = true;
        break;
      }
      case 'trending': need.trending = true; break;
      case 'favorites': need.favorites = true; break;
      case 'editorsPicks': need.picks = true; break;
      case 'continueEditing': need.recent = true; break;
      default: break;
    }
  }
  return need;
}

/**
 * Choose a preset: the page becomes the preset's sections. Saved searches
 * stay in the library (their sections go, as the preset has none); a section
 * from a newer build is kept, at the end, as the server needs it back
 * unchanged.
 */
export function applyPreset(name: NamedPreset): LayoutChange {
  const change: LayoutChange = (layout) => {
    const preset = presetOf(name, registryOf(layout));
    if (!preset) return layout;
    const kept = layout.sections.filter((s) => !isKnownType(s.type, layout));
    return { ...layout, preset: name, sections: [...preset.sections.map(copy), ...kept] };
  };
  change.setsPreset = name;
  return change;
}

/**
 * Reset to the default preset (sections and preset) while keeping the
 * saved-search library: what the page shows at once while the store sends
 * the server's reset (`POST …/reset`), which does the same.
 */
export function resetLayout(): LayoutChange {
  const change: LayoutChange = (layout) => ({
    ...layout,
    preset: 'default',
    sections: (presetOf('default', registryOf(layout))?.sections ?? DEFAULT_LAYOUT.sections).map(copy),
  });
  change.setsPreset = 'default';
  return change;
}

const NOT_EDITABLE = "That section comes from another version of brewlytics, so it can't be changed here.";

/** Change one section by id: nothing when it is gone, refused when this build does not know its type. */
function editSection(id: string, edit: (s: DiscoverSection) => DiscoverSection): LayoutChange {
  return (layout) => {
    const at = layout.sections.findIndex((s) => s.id === id);
    if (at < 0) return layout;
    const target = layout.sections[at];
    if (!isKnownType(target.type, layout)) throw new LayoutChangeError(NOT_EDITABLE);
    const sections = [...layout.sections];
    sections[at] = edit(target);
    return { ...layout, sections };
  };
}

/** Change one section by id, with the document it is in at hand. */
function editSectionIn(id: string, edit: (s: DiscoverSection, layout: DiscoverLayout) => DiscoverSection): LayoutChange {
  return (layout) => editSection(id, (s) => edit(s, layout))(layout);
}

/** The show/hide switch. */
export function setHidden(id: string, hidden: boolean): LayoutChange {
  return editSection(id, (s) => ({ ...s, hidden }));
}

/** "Show as": only a variant the type allows. */
export function setVariant(id: string, variant: DiscoverVariant): LayoutChange {
  return editSectionIn(id, (s, layout) => (variantsOf(s.type, registryOf(layout)).includes(variant) ? { ...s, variant } : s));
}

/** How many it shows, kept to the registry's range (1–24). */
export function setCount(id: string, count: number): LayoutChange {
  return editSectionIn(id, (s, layout) => ({ ...s, count: countOf({ ...s, count }, layout) }));
}

/** A saved search's filter box (#2302): auto is stored as no key at all; a setting the registry lacks, not at all. */
export function setFilterBox(id: string, mode: FilterBoxMode): LayoutChange {
  return editSectionIn(id, (s, layout) => {
    if (s.type !== 'savedSearch' || !filterBoxModesOf(layout).includes(mode)) return s;
    const { filterBox: _old, ...settings } = s.settings ?? {};
    return { ...s, settings: mode === 'auto' ? settings : { ...settings, filterBox: mode } };
  });
}

/** What the featured banner draws from. */
export function setFeaturedSource(id: string, source: FeaturedSource): LayoutChange {
  return editSection(id, (s) => (s.type === 'featured' ? { ...s, settings: { ...s.settings, sources: [...SOURCES_FOR[source]] } } : s));
}

/** The favorites section's kinds: at least one, of those the registry allows, in its order. */
export function setFavoriteKinds(id: string, kinds: FavoriteKind[]): LayoutChange {
  return editSectionIn(id, (s, layout) => {
    const order = allowedOf('favorites', 'kinds', registryOf(layout)) as FavoriteKind[];
    const next = order.filter((k) => kinds.includes(k));
    if (s.type !== 'favorites' || next.length === 0) return s;
    return { ...s, settings: { ...s.settings, kinds: next } };
  });
}

/**
 * The sheet's Move up / Move down: one place in the full list (hidden and
 * unknown sections are rows there too, unlike on the page).
 */
export function moveSectionBy(id: string, dir: -1 | 1): LayoutChange {
  return (layout) => {
    const from = layout.sections.findIndex((s) => s.id === id);
    const to = from + dir;
    if (from < 0 || to < 0 || to >= layout.sections.length) return layout;
    const sections = [...layout.sections];
    const [moved] = sections.splice(from, 1);
    sections.splice(to, 0, moved);
    return { ...layout, sections };
  };
}

/**
 * Put the sections in this order (a drag). A section the order does not
 * name (added on another device meanwhile) keeps its place after the ones
 * it followed; an id that is gone is skipped.
 */
export function arrangeSections(order: string[]): LayoutChange {
  return (layout) => {
    const byId = new Map(layout.sections.map((s) => [s.id, s]));
    const named = order.map((id) => byId.get(id)).filter((s): s is DiscoverSection => !!s);
    const listed = new Set(named.map((s) => s.id));
    const sections = [...named];
    layout.sections.forEach((s, i) => {
      if (listed.has(s.id)) return;
      const before = layout.sections.slice(0, i).reverse().find((p) => listed.has(p.id));
      sections.splice(before ? sections.indexOf(before) + 1 : 0, 0, s);
      listed.add(s.id);
    });
    return { ...layout, sections };
  };
}

/** "+ Add section" from the catalog, or the "New section available" chip: at the bottom. */
export function addCatalogSection(type: BuiltInType, hidden = false): LayoutChange {
  return (layout) => {
    const entry = catalogEntry(type, registryOf(layout));
    if (!entry || layout.sections.some((s) => s.type === type)) return layout;
    const taken = new Set(layout.sections.map((s) => s.id));
    let id = entry.section.id;
    let n = 2;
    while (taken.has(id)) {
      id = `${entry.section.id}-${n}`;
      n++;
    }
    return withSection(layout, { ...copy(entry.section), id, hidden }, 'bottom');
  };
}

// ── Problems ────────────────────────────────────────────────────────────

/** The server's 400 problems for a layout, in plain words. */
export function explainLayoutProblems(problems: string[]): string[] {
  const out = problems.map((p) => {
    const at = p.indexOf(': ');
    const key = at < 0 ? p : p.slice(0, at);
    const rest = at < 0 ? '' : p.slice(at + 2);
    const inSearch = /^searches\[\d+\]\.(.*)$/.exec(key);
    if (inSearch) {
      const field = inSearch[1];
      if (field === 'title') return `Title: ${rest}`;
      if (field === 'description') return `Description: ${rest}`;
      if (field.startsWith('query')) return explainProblem(`${field}: ${rest}`);
      return `Saved search: ${rest}`;
    }
    if (key === 'searches') return `Saved searches: ${rest}`;
    if (key === 'sections') return `Discover sections: ${rest}`;
    if (/^sections\[\d+\]/.test(key)) return `A Discover section: ${rest}`;
    if (key === 'body' && /bytes/.test(rest)) return 'Your Discover page is too large to save. Delete a saved search, or shorten a description.';
    return p;
  });
  return out.length ? out : ["Discover couldn't save that. Check the title and description and try again."];
}
